import React, {
  ChangeEvent,
  Dispatch,
  ForwardedRef,
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { NoraCheckboxInput } from 'ethos-design-system';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import {
  Box,
  Button,
  Divider,
  Drawer,
  TextField,
  Typography,
} from '@mui/material';

import MaskedInput, { MaskedInputProps } from 'react-text-mask';
import {
  ExpandedMultiSelect,
  FlexGrid,
  FlexRow,
  NoraButton,
  Select,
  Spacer,
} from '../../../../common/components/v2';
import {
  DebitsValue,
  Decision,
  DecisionType,
  isDebitsAvailable,
  TobaccoClass,
} from '../../../../common/constants/decision';
import { Products } from '../../../../common/constants/products';
import { Reason } from '../../../../common/constants/reasons';
import { Resource } from '../../../../common/constants/resource';
import ElementLocators from '../../../../common/ElementLocators';
import { ProductFactory } from '../../../../common/productConfig/productFactory';
import { IFilterItem } from '../../interfaces/IFilterItem';
import { IRiderInfo } from '../../interfaces/IRiderInfo';
import { ApprovalReasonField } from '../components/ApprovalReasonField';
import { ClrCheckbox } from '../components/ClrCheckbox';
import { DecisionUpdateReasons } from '../decisionUpdateReasons';
import { MIBCodebacks } from '../MIBCodebacks';
import { isApprovalReasonValid } from '../piaPlanFApprovalReasons';
import { RequiredFieldErrorBar } from '../RequiredFieldErrorBar';
import { RequiredFieldTitle } from '../RequiredFieldTitle';
import type { RiderExclusivityRule } from '../riderExclusivity';
import Riders from '../Riders';
import styles from '../styles.module.scss';

function checkDebits(debits: number | null, list: DebitsValue[]): boolean {
  if (debits === null) return false;

  if (debits % 25 !== 0) {
    return false;
  }

  return list.includes(debits);
}

const decisionUpdateOptions = Object.values(DecisionUpdateReasons).map(
  value => ({ value, label: value }),
);

const dollarMaskFunction = createNumberMask({
  allowDecimal: false,
  allowLeadingZeroes: false,
  includeThousandsSeparator: true,
  prefix: '$',
});

const MoneyInput = forwardRef(
  (props: MaskedInputProps, ref: ForwardedRef<MaskedInput>) => (
    <MaskedInput
      {...props}
      ref={ref}
      mask={dollarMaskFunction}
      placeholderChar={'\u2000'}
      showMask={false}
    />
  ),
);

export const EditApproval = ({
  isOpen,
  setIsOpen,
  setIsReview,
  product,
  carrier,
  subCategory,
  maxCoverage,
  setMaxCoverage,
  term,
  rateClasses,
  selectedRateClass,
  setSelectedRateClass,
  tobaccoClasses,
  selectedTobaccoClass,
  setSelectedTobaccoClass,
  paOptions,
  selectedPaOption,
  setSelectedPaOption,
  debits,
  setDebits,
  amendment,
  setAmendment,
  doctorInfo,
  setDoctorInfo,
  doctorInfoNotProvided,
  setDoctorInfoNotProvided,
  showOtaf,
  otafReasons,
  selectedOtafReasons,
  setSelectedOtafReasons,
  otafDescription,
  setOtafDescription,
  isPiaOrPlanF,
  selectedApprovalReasons,
  setSelectedApprovalReasons,
  otherApprovalReason,
  setOtherApprovalReason,
  mibCodes,
  setMibCodes,
  showTobacco,
  updateReason,
  setUpdateReason,
  isUpdate,
  setDecision,
  isPIA,
  editedDecisionType,
  isRUW,
  riders,
  setRiders,
  riderExclusivityRules = [],
  triggerClr,
  setTriggerClr,
  haltClrReasons,
  setHaltClrReasons,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setIsReview: Dispatch<SetStateAction<boolean>>;
  product: string;
  carrier: string;
  subCategory: string;
  maxCoverage: number;
  setMaxCoverage: Dispatch<SetStateAction<number>>;
  term: number;
  rateClasses: IFilterItem[];
  selectedRateClass: IFilterItem | null;
  setSelectedRateClass: Dispatch<SetStateAction<IFilterItem | null>>;
  tobaccoClasses: IFilterItem[];
  selectedTobaccoClass: IFilterItem | null;
  setSelectedTobaccoClass: Dispatch<SetStateAction<IFilterItem | null>>;
  paOptions: IFilterItem[];
  selectedPaOption: IFilterItem;
  setSelectedPaOption: Dispatch<SetStateAction<IFilterItem>>;
  debits: number | null;
  setDebits: Dispatch<SetStateAction<number | null>>;
  amendment: string;
  setAmendment: Dispatch<SetStateAction<string>>;
  doctorInfo: string;
  setDoctorInfo: Dispatch<SetStateAction<string>>;
  doctorInfoNotProvided: boolean;
  setDoctorInfoNotProvided: Dispatch<SetStateAction<boolean>>;
  showOtaf: boolean;
  otafReasons: Reason[];
  selectedOtafReasons: string[];
  setSelectedOtafReasons: Dispatch<SetStateAction<string[]>>;
  otafDescription: string;
  setOtafDescription: Dispatch<SetStateAction<string>>;
  isPiaOrPlanF: boolean;
  selectedApprovalReasons: string[];
  setSelectedApprovalReasons: Dispatch<SetStateAction<string[]>>;
  otherApprovalReason: string;
  setOtherApprovalReason: Dispatch<SetStateAction<string>>;
  mibCodes: string[];
  setMibCodes: Dispatch<SetStateAction<string[]>>;
  showTobacco: boolean;
  updateReason: string | null | undefined;
  setUpdateReason: Dispatch<SetStateAction<string | null>> | undefined;
  isUpdate: boolean;
  setDecision?: Dispatch<SetStateAction<Decision | null>> | undefined;
  isPIA: boolean;
  editedDecisionType: DecisionType | undefined;
  isRUW: boolean;
  riders?: IRiderInfo[];
  setRiders: Dispatch<SetStateAction<IRiderInfo[]>>;
  riderExclusivityRules?: RiderExclusivityRule[];
  triggerClr: boolean;
  setTriggerClr: Dispatch<SetStateAction<boolean>>;
  haltClrReasons: string[];
  setHaltClrReasons: Dispatch<SetStateAction<string[]>>;
}) => {
  // PIA / Plan F pick the reason from a dropdown; everything else types it
  const otafReasonValid = isPiaOrPlanF
    ? isApprovalReasonValid(selectedApprovalReasons, otherApprovalReason)
    : !!otafDescription;
  const otafValid = showOtaf
    ? selectedOtafReasons.length > 0 && otafReasonValid
    : true;

  // When CLR is halted (triggerClr = false) in PIA, at least one reason must be selected
  const haltClrValid = !isPIA || triggerClr || haltClrReasons.length > 0;

  const productConfig = ProductFactory.getProductConfig(
    carrier,
    product,
    subCategory,
  );

  const shouldDisplayPa = productConfig?.showPercentageAddition(
    selectedRateClass?.label,
  );

  const shouldDisplayDebits = productConfig?.showDebits(
    selectedRateClass?.label,
  );

  const paValid = shouldDisplayPa ? selectedPaOption : true;

  const debitsValid: boolean =
    shouldDisplayPa || shouldDisplayDebits
      ? checkDebits(debits, productConfig?.getDebitsList() || [])
      : true;

  const debitsList = (productConfig?.getDebitsList() || []).map(debitsValue => {
    return { value: debitsValue, label: debitsValue };
  });
  const isIulProduct = [Products.IUL, Products.NAIULACUM].includes(
    product as Products,
  );

  const toDebitsList = (value: number | null) => {
    const selectedItems = debitsList.filter(t => t.value === value);
    if (selectedItems.length === 1) {
      setDebitsAndPa(value);
      return selectedItems[0];
    } else if (!isRUW) {
      setDebitsAndPa(DebitsValue.Debits_0);
      return debitsList.filter(t => t.value === DebitsValue.Debits_0)[0];
    } else {
      return null;
    }
  };

  const [showError, setShowError] = useState(false);

  useEffect(() => {
    // If the drawer is closed and then re-opened, the error bar should re-set
    setShowError(false);
  }, [isOpen]);

  const decisionOptions = [
    { value: Decision.DECLINED, label: Resource.DECLINE_OPTION },
    { value: Decision.APPROVED, label: Resource.APPROVE_OPTION },
    ...(isPIA
      ? [
          { value: Decision.INELIGIBLE, label: Resource.INELIGIBLE_OPTION },
          { value: Decision.WAIVED, label: Resource.WAIVED_OPTION },
          { value: Decision.POSTPONED, label: Resource.POSTPONE_OPTION },
        ]
      : []),
  ];

  function setDebitsAndPa(debitsNum: number | null) {
    setDebits(debitsNum);
    if (debitsNum === null) return;
    if (productConfig?.hasTableRating()) {
      // debits and pa should always in sync
      const paValue = productConfig?.getPa(debitsNum);
      const paStringValue = String(paValue);
      for (const pa of paOptions) {
        if (paStringValue === pa.label) {
          setSelectedPaOption(pa);
          break;
        }
      }
    }
  }

  const handleOnMaxCoverageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.currentTarget.value;
      const numericalValue = newValue.replace(/[^\d\.]/g, '');
      setMaxCoverage(Number(numericalValue));
    },
    [setMaxCoverage],
  );

  const handleRateClassChange = useCallback(
    (rate: IFilterItem) => {
      setSelectedRateClass(rate);
      if (isDebitsAvailable(rate.label)) return;
      setDebitsAndPa(DebitsValue.Debits_0);
    },
    [setSelectedRateClass, setDebitsAndPa],
  );

  const reasonValue = useMemo(() => {
    if (!updateReason) return null;
    return { label: updateReason, value: updateReason };
  }, [updateReason]);

  return (
    <Drawer
      open={isOpen}
      anchor="right"
      variant="persistent"
      classes={{ paper: styles.Drawer }}
      transitionDuration={{ enter: 0, exit: 0 }}
    >
      <Box mt={6}>
        <Box
          m={3}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body1">
            {isUpdate
              ? Resource.UPDATE_DECISION_HEADER
              : Resource.APPROVE_APPLICATION_HEADER}
          </Typography>
          <Button onClick={() => setIsOpen(false)}>
            <Typography variant="subtitle2" color="textSecondary">
              Cancel
            </Typography>
          </Button>
        </Box>
        <Divider />
        <Box m={3} className={styles.DrawerFormContent}>
          <FlexGrid>
            <Spacer height={16} />
            {showError && (
              <>
                <RequiredFieldErrorBar />
                <Spacer height={8} />
              </>
            )}
            {isUpdate && (
              <>
                <Spacer height={16} />
                <FlexRow>
                  <Select
                    fullWidth
                    size="small"
                    variant="outlined"
                    title="Reason for update"
                    placeholder={Resource.SELECT_PLACEHOLDER}
                    defaultValue={reasonValue}
                    value={reasonValue}
                    onChange={({ value }) => setUpdateReason?.(value as string)}
                    options={decisionUpdateOptions}
                  />
                </FlexRow>
                <Spacer height={16} />
                <FlexRow>
                  <Select
                    fullWidth
                    size="small"
                    variant="outlined"
                    title="Decision"
                    placeholder={Resource.SELECT_PLACEHOLDER}
                    defaultValue={reasonValue}
                    value={{
                      value: Decision.APPROVED,
                      label: Resource.APPROVE_OPTION,
                    }}
                    onChange={({ value }) => setDecision?.(value as Decision)}
                    options={decisionOptions}
                  />
                </FlexRow>
              </>
            )}
            {!isUpdate && <RequiredFieldTitle title={Resource.APPROVE_AT} />}
            <Spacer height={8} />
            <FlexRow>
              <TextField
                size="small"
                defaultValue={maxCoverage}
                name="max-coverage-number-input"
                label="Max Coverage"
                data-tid="max-coverage-number-input"
                value={maxCoverage}
                onChange={handleOnMaxCoverageChange}
                variant="outlined"
                margin="dense"
                fullWidth
                disabled={isPIA}
                InputProps={{ inputComponent: MoneyInput }}
              />
            </FlexRow>
            <Spacer height={2} />
            {!isIulProduct && (
              <FlexRow>
                <TextField
                  size="small"
                  defaultValue={term}
                  label="Term Length"
                  value={term}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  disabled
                />
              </FlexRow>
            )}
            {productConfig?.hasRateClass() && (
              <>
                <Spacer height={14} />
                <FlexRow>
                  <Select
                    size="small"
                    variant="outlined"
                    fullWidth
                    defaultValue={selectedRateClass}
                    value={selectedRateClass}
                    onChange={handleRateClassChange}
                    options={rateClasses}
                    title="Class"
                  />
                </FlexRow>
              </>
            )}
            {(shouldDisplayDebits || shouldDisplayPa) && (
              <>
                <Spacer height={14} />
                <FlexRow>
                  <Select
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={toDebitsList(debits)}
                    defaultValue={toDebitsList(debits)}
                    onChange={({ value }) => setDebitsAndPa(value)}
                    options={debitsList}
                    title="Debits"
                  />
                </FlexRow>
              </>
            )}
            {showTobacco && productConfig?.hasRateClass() && (
              <>
                <Spacer height={14} />
                <FlexRow>
                  <Select
                    size="small"
                    variant="outlined"
                    fullWidth
                    defaultValue={selectedTobaccoClass}
                    value={selectedTobaccoClass}
                    onChange={setSelectedTobaccoClass}
                    options={tobaccoClasses}
                    title="Tobacco Class"
                  />
                </FlexRow>
              </>
            )}
            {shouldDisplayPa && (
              <>
                <Spacer height={8} />
                <FlexRow>
                  <TextField
                    size="small"
                    defaultValue={selectedPaOption.label}
                    label="% Additional"
                    value={selectedPaOption.label}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    disabled
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </FlexRow>
              </>
            )}
            {riders && riders.length > 0 && (
              <Riders
                riders={riders}
                setRiders={setRiders}
                exclusivityRules={riderExclusivityRules}
              />
            )}
            <Spacer height={12} />
            <MIBCodebacks
              label={Resource.MIB_CODES}
              value={mibCodes}
              onChange={setMibCodes}
            />
            <Spacer height={16} />
            {isPIA && (
              <ClrCheckbox
                editedDecisionType={editedDecisionType}
                triggerClr={triggerClr}
                setTriggerClr={setTriggerClr}
                haltClrReasons={haltClrReasons}
                setHaltClrReasons={setHaltClrReasons}
              />
            )}
            {showOtaf && (
              <>
                <Spacer height={12} />
                <FlexRow>
                  <ExpandedMultiSelect
                    required
                    options={otafReasons.map(item => ({
                      label: item.displayName,
                      value: item.key,
                    }))}
                    label="Otaf reasons"
                    value={selectedOtafReasons}
                    onChange={setSelectedOtafReasons}
                  />
                </FlexRow>
                <Spacer height={24} />
                {isPiaOrPlanF ? (
                  <ApprovalReasonField
                    selected={selectedApprovalReasons}
                    setSelected={setSelectedApprovalReasons}
                    otherText={otherApprovalReason}
                    setOtherText={setOtherApprovalReason}
                    showRequiredErrors={showError}
                  />
                ) : (
                  <FlexRow>
                    <TextField
                      slotProps={{ inputLabel: { shrink: true } }}
                      minRows={4}
                      multiline
                      name={ElementLocators.LOCATOR_OTAF_REASONS_INPUT}
                      data-tid={ElementLocators.LOCATOR_OTAF_REASONS_TEXT_AREA}
                      placeholder="Describe the reason"
                      size="small"
                      defaultValue={term}
                      label="Reason"
                      value={otafDescription}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      onChange={event =>
                        setOtafDescription(event.currentTarget.value)
                      }
                    />
                  </FlexRow>
                )}
              </>
            )}
            {!isPiaOrPlanF && (
              <>
                <Spacer height={4} />
                <FlexRow>
                  <TextField
                    slotProps={{ inputLabel: { shrink: true } }}
                    minRows={4}
                    multiline
                    name={ElementLocators.LOCATOR_AMENDMENT_TEXT_AREA}
                    data-tid="amendment-textarea"
                    placeholder="Describe the reason"
                    size="small"
                    defaultValue={amendment}
                    label="Amendment"
                    value={amendment}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    onChange={event => setAmendment(event.currentTarget.value)}
                  />
                </FlexRow>
                <Spacer height={4} />
                <FlexRow>
                  <TextField
                    slotProps={{ inputLabel: { shrink: true } }}
                    minRows={4}
                    multiline
                    name={ElementLocators.LOCATOR_DOCTORS_INFO_INPUT}
                    disabled={doctorInfoNotProvided}
                    data-tid={ElementLocators.LOCATOR_DOCTORS_INFO_TEXT_AREA}
                    placeholder="Name, address, zipcode and phone number&#10;&#10;Include as many physicians or facilities as possible"
                    size="small"
                    defaultValue={doctorInfo}
                    label="Doctor's Info (Post Issue APS)"
                    value={doctorInfo}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    onChange={event => setDoctorInfo(event.currentTarget.value)}
                  />
                  <NoraCheckboxInput
                    name="doctors-info-unavailable"
                    data-tid="doctors-info-unavailable"
                    checked={doctorInfoNotProvided}
                    validator={val => {
                      const flag = !!val;
                      setDoctorInfoNotProvided(flag);
                      setDoctorInfo(flag ? Resource.NORA_NOT_APPLICABLE : '');
                      return '';
                    }}
                  >
                    <div className={styles.ChecklistOption}>
                      {Resource.DOCTOR_INFO_NOT_PROVIDED}
                    </div>
                  </NoraCheckboxInput>
                </FlexRow>
              </>
            )}
          </FlexGrid>
        </Box>
      </Box>
      <Box>
        <NoraButton
          variant="primary"
          name={ElementLocators.LOCATOR_REVIEW_APPROVAL_BUTTON}
          className={styles.DrawerButton}
          onClick={() => {
            if (
              otafValid &&
              haltClrValid &&
              (isPiaOrPlanF || (doctorInfo && doctorInfo.trim().length)) &&
              maxCoverage &&
              selectedRateClass &&
              (showTobacco ? !!selectedTobaccoClass : true) &&
              paValid &&
              debitsValid &&
              (isUpdate ? updateReason !== null : true)
            ) {
              if (!showTobacco)
                setSelectedTobaccoClass({
                  value: 2,
                  label: TobaccoClass.NONTOBACCO,
                });
              setIsReview(true);
            } else {
              setShowError(true);
            }
          }}
        >
          {isUpdate ? Resource.REVIEW_DECISION : Resource.REVIEW_APPROVAL}
        </NoraButton>
      </Box>
    </Drawer>
  );
};
